/**
 * PreferencesDialog — Beautiful two-slide navigation for user preferences
 * 
 * MAIN VIEW (Slide 1):
 * - Displays preference categories as gorgeous gradient cards
 * - Each card has an icon, title, description, and chevron
 * - Smooth hover effects (lift, glow, border color pulse)
 * - Categories: Task Creation, Notifications (native only)
 * 
 * DETAIL VIEWS (Slide 2):
 * - Zooms/fades in when a category is clicked
 * - Shows selectable options with descriptions
 * - Back button returns to main view with zoom/fade animation
 * - Immediate save to backend when preference changes
 * - Fixed height prevents dialog jumping
 * 
 * PREFERENCES:
 * - Task Creation: "guided" (step-by-step) or "form" (quick)
 * - Notifications: Opens nested NotificationSettingsDialog (native only)
 * 
 * DESIGN:
 * - Consistent with TaskGuide/Orb aesthetic
 * - Clean light backgrounds with subtle color overlays
 * - Smooth zoom + fade animations (no height jumping)
 * - Brand colors: #197B68 (primary), #813773 (purple), #276580 (blue)
 */

import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  IconButton,
  Fade,
  Zoom,
  ButtonBase,
} from "@mui/material";
import {
  Close as CloseIcon,
  ChevronRight as ChevronRightIcon,
  ArrowBack as ArrowBackIcon,
  AutoFixHigh as AutoFixHighIcon,
  ViewList as ViewListIcon,
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { Capacitor } from "@capacitor/core";
import { useSelector } from "react-redux";
import useUserAPIClient from "../../api/user/hooks/useUserAPIClient";
import { dispatch } from "../../redux/store";
import { setTaskCreationPreference } from "../../redux/actions/user";
import NotificationSettingsDialog from "./NotificationSettingsDialog";

/**
 * Category card for the main preference list
 */
const PreferenceCategory = ({ icon, title, description, onClick, color }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      p: 2.5,
      borderRadius: 3,
      border: "1px solid rgba(0,0,0,0.08)",
      background: "linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)",
      textAlign: "left",
      width: "100%",
      transition: "all 0.3s ease",
      "&:hover": {
        backgroundColor: "#fafafa",
        border: `1px solid ${color}60`,
        transform: "translateY(-2px)",
        boxShadow: `0 4px 12px ${color}30`,
      },
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`,
        color: color,
        fontSize: 24,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography
        variant="body1"
        sx={{ fontWeight: 600, fontSize: 16, color: "#333", mb: 0.5 }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "#666", fontSize: 13 }}
      >
        {description}
      </Typography>
    </Box>
    <ChevronRightIcon sx={{ color: "#999", fontSize: 28 }} />
  </ButtonBase>
);

/**
 * Selectable option within a preference detail view
 */
const PreferenceOption = ({ icon, title, description, selected, onClick }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      p: 2,
      borderRadius: 2,
      border: selected
        ? "2px solid #197B68"
        : "1px solid rgba(0,0,0,0.12)",
      backgroundColor: selected
        ? "rgba(25,123,104,0.08)"
        : "#fafafa",
      textAlign: "left",
      width: "100%",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: selected
          ? "rgba(25,123,104,0.12)"
          : "#f5f5f5",
      },
    }}
  >
    <Box sx={{ color: selected ? "#197B68" : "#666" }}>
      {icon}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography
        variant="body1"
        sx={{ fontWeight: 600, fontSize: 14, color: "#333" }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "#666", fontSize: 12, mt: 0.5 }}
      >
        {description}
      </Typography>
    </Box>
    {selected && (
      <CheckCircleIcon sx={{ color: "#197B68", fontSize: 22 }} />
    )}
  </ButtonBase>
);

/**
 * Task Creation detail view
 */
const TaskCreationDetail = ({ value, onChange }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
    <Typography
      variant="body2"
      sx={{ color: "#666", mb: 1, lineHeight: 1.6 }}
    >
      Choose how you'd like to create new tasks. You can change this at any time.
    </Typography>
    <PreferenceOption
      icon={<AutoFixHighIcon />}
      title="Step-by-step (Guided)"
      description="A friendly walkthrough with Ori that explains each field as you go"
      selected={value === "guided"}
      onClick={() => onChange("guided")}
    />
    <PreferenceOption
      icon={<ViewListIcon />}
      title="Quick Form"
      description="All fields on one page for faster task creation"
      selected={value === "form"}
      onClick={() => onChange("form")}
    />
  </Box>
);

/**
 * Preferences dialog with slide navigation
 */
const PreferencesDialog = ({ open, onClose }) => {
  const taskCreationPreference = useSelector(
    (state) => state.user.taskCreationPreference,
  );
  const { setTaskCreationPreference: saveTaskCreationPref } = useUserAPIClient();
  const [notificationSettingsOpen, setNotificationSettingsOpen] = useState(false);
  const [currentView, setCurrentView] = useState("main"); // 'main' | 'taskCreation'

  const handleNavigateToDetail = (view) => {
    setCurrentView(view);
  };

  const handleNavigateBack = () => {
    setCurrentView("main");
  };

  const handleTaskCreationChange = async (preference) => {
    if (preference === taskCreationPreference) return;
    dispatch(setTaskCreationPreference(preference));
    saveTaskCreationPref(preference).catch((err) =>
      console.error("Failed to save task creation preference:", err),
    );
  };

  const handleNotificationsClick = () => {
    setNotificationSettingsOpen(true);
  };

  const handleClose = () => {
    setCurrentView("main");
    onClose();
  };

  // Get current view title
  const getTitle = () => {
    switch (currentView) {
      case "taskCreation":
        return "Task Creation";
      default:
        return "Preferences";
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            background: "#ffffff",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #197b68 0%, #004a3c 100%)",
            py: 2.5,
            px: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
            position: "relative",
          }}
        >
          {currentView !== "main" && (
            <IconButton
              onClick={handleNavigateBack}
              sx={{
                color: "#fff",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography
            variant="h6"
            sx={{
              color: "#fff",
              fontWeight: 700,
              fontSize: 20,
              flex: 1,
            }}
          >
            {getTitle()}
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <DialogContent
          sx={{
            position: "relative",
            overflow: "hidden",
            minHeight: 400,
            height: 400,
            p: 3,
            background: "linear-gradient(180deg, rgba(25,123,104,0.02) 0%, transparent 50%, rgba(129,55,115,0.02) 100%)",
          }}
        >
          {/* Main View */}
          <Zoom in={currentView === "main"} timeout={300} unmountOnExit>
            <Box sx={{ position: "absolute", inset: "24px", overflow: "auto" }} className="scroller">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <PreferenceCategory
                  icon={<AutoFixHighIcon />}
                  title="Task Creation"
                  description="Choose between guided or quick task creation"
                  onClick={() => handleNavigateToDetail("taskCreation")}
                  color="#813773"
                />
                {Capacitor.isNativePlatform() && (
                  <PreferenceCategory
                    icon={<NotificationsIcon />}
                    title="Notifications"
                    description="Configure reminders, deadlines, and quiet hours"
                    onClick={handleNotificationsClick}
                    color="#197B68"
                  />
                )}
              </Box>
            </Box>
          </Zoom>

          {/* Task Creation Detail */}
          <Zoom in={currentView === "taskCreation"} timeout={300} unmountOnExit>
            <Box sx={{ position: "absolute", inset: "24px", overflow: "auto" }} className="scroller">
              <TaskCreationDetail
                value={taskCreationPreference}
                onChange={handleTaskCreationChange}
              />
            </Box>
          </Zoom>
        </DialogContent>
      </Dialog>

      <NotificationSettingsDialog
        open={notificationSettingsOpen}
        onClose={() => setNotificationSettingsOpen(false)}
      />
    </>
  );
};

export default PreferencesDialog;

